// ============================================================
// ✅ DemandesConge.jsx - V5 - Tableau historique + Filtres
// + Blocage observateur (rôle dédié)
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { 
  Plus, Clock, Check, X, Eye, Send, Trash2, RotateCcw, Search,
  ChevronLeft, ChevronRight, EyeOff
} from 'lucide-react';
import NouvelleDemandeModal from './NouvelleDemandeModal';
import SuiviDemandeModal from './SuiviDemandeModal';

export default function DemandesConge() {
  const { user } = useUser();

  // ✅ Détecter l'observateur via le rôle dédié
  const isObservateur = user?.Role === 'observateur';

  // ==========================================================
  // 📊 ÉTATS
  // ==========================================================
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [selectedDemande, setSelectedDemande] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmDemande, setConfirmDemande] = useState(null);

  const [stats, setStats] = useState({ Total: 0, EnAttente: 0, Approuvees: 0, Refusees: 0, JoursConsommes: 0 });

  // ==========================================================
  // 🔄 CHARGEMENT
  // ==========================================================
  useEffect(() => {
    if (user?.id && !isObservateur) { fetchDemandes(); fetchKpi(); }
  }, [user, currentYear]);

  const fetchDemandes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/demandes/employe/${user.id}`, { params: { annee: currentYear } });
      setDemandes(res.data.demandes || []);
    } catch (err) { /* Silencieux */ }
    finally { setLoading(false); }
  };

  const fetchKpi = async () => {
    try {
      const res = await api.get(`/demandes/kpi/${user.id}`, { params: { annee: currentYear } });
      if (res.data.success) setStats(res.data.stats || { Total: 0, EnAttente: 0, Approuvees: 0, Refusees: 0, JoursConsommes: 0 });
    } catch (err) { /* Silencieux */ }
  };

  // ==========================================================
  // 🖱️ HANDLERS
  // ==========================================================
  const handleViewSuivi = (demande) => { setSelectedDemande(demande); setShowSuiviModal(true); };
  const handleConfirmer = (d, e) => { if (e) e.stopPropagation(); setConfirmDemande(d); setConfirmAction('confirmer'); setShowConfirmModal(true); };
  const handleSupprimer = (d, e) => { if (e) e.stopPropagation(); setConfirmDemande(d); setConfirmAction('supprimer'); setShowConfirmModal(true); };
  const handleAnnuler = (d, e) => { if (e) e.stopPropagation(); setConfirmDemande(d); setConfirmAction('annuler'); setShowConfirmModal(true); };

  const executeConfirmAction = async () => {
    if (!confirmDemande) return;
    if (confirmAction === 'confirmer') {
      try { const res = await api.post(`/demandes/confirmer/${confirmDemande.ID_DdeConges}`, { ID_Employe: user.id }); if (res.data.success) { fetchDemandes(); fetchKpi(); } else { alert(res.data.message); } } catch (err) { alert(err.response?.data?.message); }
    } else if (confirmAction === 'supprimer') {
      try { await api.delete(`/demandes/${confirmDemande.ID_DdeConges}`); fetchDemandes(); fetchKpi(); } catch (err) { alert(err.response?.data?.message); }
    } else if (confirmAction === 'annuler') {
      try { const res = await api.put(`/demandes/${confirmDemande.ID_DdeConges}/annuler`, { ID_Employe: user.id }); if (res.data.success) { fetchDemandes(); fetchKpi(); } else { alert(res.data.message); } } catch (err) { alert(err.response?.data?.message); }
    }
    setShowConfirmModal(false); setConfirmDemande(null); setConfirmAction(null);
  };

  const getStatusBadge = (statusId) => {
    switch (statusId) {
      case 1: return { bg: 'bg-gray-100', text: 'text-gray-600', icon: '📝', label: 'Brouillon' };
      case 2: return { bg: 'bg-amber-100', text: 'text-amber-700', icon: '⏳', label: 'En attente' };
      case 3: return { bg: 'bg-green-100', text: 'text-green-700', icon: '✅', label: 'Approuvé' };
      case 4: return { bg: 'bg-red-100', text: 'text-red-700', icon: '❌', label: 'Refusé' };
      case 5: return { bg: 'bg-purple-100', text: 'text-purple-700', icon: '🔄', label: 'Annulé' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-500', icon: '?', label: 'Inconnu' };
    }
  };

  // ==========================================================
  // 📊 FILTRES
  // ==========================================================
  const filteredDemandes = demandes.filter(d => {
    const matchSearch = !searchTerm || 
      d.TypeCongeLibelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.DateDebutFormatee?.includes(searchTerm) ||
      d.DateFinFormatee?.includes(searchTerm);
    const matchStatus = filterStatus === 'all' || d.StatusId === parseInt(filterStatus);
    return matchSearch && matchStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDemandes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDemandes.length / itemsPerPage);

  // ==========================================================
  // 🚫 OBSERVATEUR : Page bloquée
  // ==========================================================
  if (isObservateur) {
    return (
      <div className="p-3 md:p-5 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <EyeOff size={36} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Accès restreint</h2>
          <p className="text-sm text-gray-500">
            En tant qu'observateur, vous n'avez pas accès à la gestion des demandes de congés.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Votre accès est limité à la consultation du planning depuis le tableau de bord.
          </p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // 🎨 RENDU NORMAL
  // ==========================================================
  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen">

      {/* EN-TÊTE */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-base md:text-lg font-bold text-gray-800">📋 Mes demandes</h2>
          <p className="text-[11px] text-gray-400">Historique de vos demandes de congés</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setCurrentYear(y => y - 1); setCurrentPage(1); }} className="p-1 text-gray-400 hover:text-emerald-600"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-gray-700">{currentYear}</span>
          <button onClick={() => { setCurrentYear(y => y + 1); setCurrentPage(1); }} className="p-1 text-gray-400 hover:text-emerald-600"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {[
          { label: 'Total', value: stats.Total, color: 'text-gray-700', bg: 'bg-white' },
          { label: 'En attente', value: stats.EnAttente, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Approuvés', value: stats.Approuvees, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Refusés', value: stats.Refusees, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Jours pris', value: stats.JoursConsommes || 0, color: 'text-blue-600', bg: 'bg-blue-50' }
        ].map((item, idx) => (
          <div key={idx} className={`${item.bg} rounded-lg p-2 text-center shadow-sm border border-gray-100`}>
            <p className="text-[10px] text-gray-400">{item.label}</p>
            <p className={`text-base font-bold ${item.color}`}>{item.value || 0}</p>
          </div>
        ))}
      </div>

      {/* RECHERCHE + FILTRES + BOUTON */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" placeholder="Rechercher par type ou date..." className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500"
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <div className="flex gap-1">
          {[{ id: 'all', label: 'Tous' }, { id: '2', label: '⏳ En attente' }, { id: '3', label: '✅ Approuvés' }, { id: '4', label: '❌ Refusés' }, { id: '5', label: '🔄 Annulés' }].map(f => (
            <button key={f.id} onClick={() => { setFilterStatus(f.id); setCurrentPage(1); }}
              className={`px-2 py-1.5 text-[10px] rounded-lg font-medium transition whitespace-nowrap ${filterStatus === f.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* ✅ Bouton "Nouvelle demande" masqué pour admin et observateur */}
        {user?.Role !== 'admin' && !isObservateur && (
          <button onClick={() => setShowCreateModal(true)} className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center gap-1 transition shadow-sm whitespace-nowrap">
            <Plus size={14} /> Nouvelle demande
          </button>
        )}
      </div>

      {/* TABLEAU HISTORIQUE */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-500">
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Période</th>
                <th className="px-3 py-2 text-center">Jours</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400">Chargement...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400">Aucune demande</td></tr>
              ) : (
                currentItems.map(d => {
                  const status = getStatusBadge(d.StatusId);
                  return (
                    <tr key={d.ID_DdeConges} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewSuivi(d)}>
                      <td className="px-3 py-2 font-medium text-gray-700">{d.TypeCongeLibelle}</td>
                      <td className="px-3 py-2 text-gray-500">{d.DateDebutFormatee} → {d.DateFinFormatee}</td>
                      <td className="px-3 py-2 text-center font-medium">{d.TotalDays} j</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${status.bg} ${status.text}`}>
                          {status.icon} {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {d.StatusId === 1 && (
                            <>
                              <button onClick={(e) => handleConfirmer(d, e)} className="p-1 text-emerald-500 hover:text-emerald-700" title="Envoyer"><Send size={13} /></button>
                              <button onClick={(e) => handleSupprimer(d, e)} className="p-1 text-red-400 hover:text-red-600" title="Supprimer"><Trash2 size={13} /></button>
                            </>
                          )}
                          {d.StatusId === 3 && new Date(d.DateDebut) > new Date() && (
                            <button onClick={(e) => handleAnnuler(d, e)} className="p-1 text-purple-500 hover:text-purple-700" title="Annuler"><RotateCcw size={13} /></button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleViewSuivi(d); }} className="p-1 text-blue-400 hover:text-blue-600" title="Voir suivi"><Eye size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t flex justify-between items-center">
            <p className="text-[10px] text-gray-400">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredDemandes.length)} sur {filteredDemandes.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded disabled:opacity-30 hover:bg-gray-200"><ChevronLeft size={14} /></button>
              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{currentPage}/{totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded disabled:opacity-30 hover:bg-gray-200"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* MODALES */}
      <NouvelleDemandeModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={() => { fetchDemandes(); fetchKpi(); }} />
      <SuiviDemandeModal isOpen={showSuiviModal} onClose={() => setShowSuiviModal(false)} demande={selectedDemande} />

      {/* MODALE DE CONFIRMATION */}
      {showConfirmModal && confirmDemande && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowConfirmModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              {confirmAction === 'confirmer' && <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto"><Send size={28} className="text-emerald-600" /></div>}
              {confirmAction === 'supprimer' && <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto"><Trash2 size={28} className="text-red-500" /></div>}
              {confirmAction === 'annuler' && <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto"><RotateCcw size={28} className="text-purple-500" /></div>}
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
              {confirmAction === 'confirmer' ? "Confirmer l'envoi" : confirmAction === 'supprimer' ? 'Supprimer le brouillon' : 'Annuler la demande'}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-2">
              {confirmAction === 'confirmer' && 'La demande sera verrouillée et transmise pour validation.'}
              {confirmAction === 'supprimer' && 'Ce brouillon sera définitivement supprimé.'}
              {confirmAction === 'annuler' && 'Les jours seront remis dans votre solde.'}
            </p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-xs">
              <p className="font-semibold">{confirmDemande.TypeCongeLibelle}</p>
              <p className="text-gray-500">{confirmDemande.DateDebutFormatee} → {confirmDemande.DateFinFormatee}</p>
              <p className="text-gray-400">{confirmDemande.TotalDays} jour(s)</p>
            </div>
            <p className="text-xs text-amber-600 text-center mb-4">⚠️ Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirmModal(false)} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium">Annuler</button>
              <button onClick={executeConfirmAction} className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-medium ${confirmAction === 'confirmer' ? 'bg-emerald-500' : confirmAction === 'supprimer' ? 'bg-red-500' : 'bg-purple-500'}`}>
                {confirmAction === 'confirmer' ? '📤 Envoyer' : confirmAction === 'supprimer' ? '🗑️ Supprimer' : '🔄 Annuler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}